import Image from "next/image";
import React from "react";
import PropTypes from "prop-types";

const CandidateLoginContent = ({ companyDetails, theme }) => {
  const myLoader = ({ src }) => src;
  
  // Determine if we should show legacy "WELCOME TO" layout
  // Show legacy ONLY when template is undefined (not set)
  const showLegacyWelcome = !theme?.template;
  const companyName = companyDetails?.name || companyDetails?.brandName || "Company";
  
  // Logo logic: Use theme logo, fallback to company logo
  const logoSrc = theme?.loginLogo || companyDetails?.logo;
  
  const shouldShowLogo = showLegacyWelcome || Boolean(logoSrc);

  // Legacy "WELCOME TO Company" layout
  if (showLegacyWelcome) {
    return (
      <div className="relative h-full w-full overflow-hidden bg-Mist">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center z-[10]"
            style={{ backgroundImage: 'url(/images/Login-Page.png)' }}
          />
          <div className="absolute inset-0 bg-gray-200/60 z-[11]"></div>
        </div>

        <div className="relative z-20 h-full flex flex-col">
          {/* Logo in top-left - responsive sizing */}
          {shouldShowLogo && (
            <div className="flex items-start justify-start px-4 sm:px-6 md:px-8 lg:px-12 pt-6 md:pt-8">
              <div className="relative w-20 h-10 sm:w-24 sm:h-12 md:w-28 md:h-14 lg:min-w-[70px] lg:max-w-[140px] lg:min-h-[36px]">
                <Image
                  loader={myLoader}
                  src={logoSrc}
                  alt={`${companyName} logo`}
                  layout="fill"
                  objectFit="contain"
                />
              </div>
            </div>
          )}

           {/* Centered "WELCOME TO Company." text - responsive */}
           <div className="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 md:px-8 lg:px-12 py-6 md:py-8 -mt-16 md:-mt-20">
             <div className="w-full text-center">
               <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light md:font-thin tracking-wide text-black mb-3 sm:mb-4 md:mb-6 leading-tight">
                 WELCOME TO
               </p>
               <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-wide text-black leading-tight break-words">
                 {companyName}
               </p>
             </div>
           </div>
        </div>
      </div>
    );
  }

  // Themed layout with hero image and marketing content
  const heroImage = theme?.heroImage || "/images/Login-Page.png";
  const template = theme.template;
  const showText = template !== "none";
  const textJustify =
    template === "top"
      ? "justify-start"
      : template === "bottom"
      ? "justify-end"
      : "justify-center";

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div className="absolute inset-0">
        <Image
          loader={myLoader}
          src={heroImage}
          alt="Hero"
          layout="fill"
          objectFit="cover"
          objectPosition="top center"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/8 to-black/25 md:from-black/12" />
      </div>

      <div className="relative z-10 h-full flex flex-col">
        {shouldShowLogo && (
          <div className="flex items-start justify-start px-4 sm:px-6 md:px-8 lg:px-12 pt-4 sm:pt-6 md:pt-8">
            <div className="relative w-20 h-10 sm:w-28 sm:h-14 md:w-32 md:h-16 lg:min-w-[70px] lg:max-w-[140px] lg:min-h-[36px]">
              <Image
                id="img_tag_148"
                loader={myLoader}
                src={logoSrc}
                alt="logo"
                layout="fill"
                objectFit="contain"
              />
            </div>
          </div>
        )}

        <div
          className={`flex-1 flex flex-col items-start ${textJustify} px-4 sm:px-6 md:px-8 lg:px-12 pb-12 ${shouldShowLogo ? 'pt-6' : 'pt-16 md:pt-24 lg:pt-28'}`}
        >
          {showText && (
            <div className="w-full max-w-3xl text-[color:var(--login-heading)] drop-shadow-sm hidden md:block">
              <h1 className="font-bold text-3xl sm:text-4xl md:text-4xl lg:text-5xl leading-tight mb-4 break-words">
                {theme?.heading1Text}
              </h1>
              {theme?.heading2Text && (
                <p className="text-sm sm:text-base md:text-base lg:text-lg text-[color:var(--login-heading)] opacity-90 mb-3 max-w-2xl">
                  {theme.heading2Text}
                </p>
              )}
              {theme?.heading3Text && (
                <p className="text-xs sm:text-sm md:text-sm lg:text-base text-[color:var(--login-heading)] opacity-80 max-w-2xl">
                  {theme.heading3Text}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateLoginContent;

CandidateLoginContent.propTypes = {
  companyDetails: PropTypes.shape({
    logo: PropTypes.string,
    brandName: PropTypes.string,
    slug: PropTypes.string,
  }),
  theme: PropTypes.shape({
    heroImage: PropTypes.string,
    mobileHeroImage: PropTypes.string,
    headline: PropTypes.string,
    subheadline: PropTypes.string,
    supportingText: PropTypes.string,
    highlights: PropTypes.arrayOf(PropTypes.string),
    textDisplayArea: PropTypes.oneOf(["top", "bottom", "none"]),
  }),
};
